import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import ArrowsLine from '../assets/icons/ArrowsLine';
import Copy from '../assets/icons/Copy';
import Wallet from '../assets/icons/Wallet';
import BackButton from '../components/ui/BackButton';
import Button from '../components/ui/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '../components/ui/Form';
import Input from '../components/ui/Input';
import Range from '../components/ui/Range';
import Tag from '../components/ui/Tag';
import { copyToClipboard } from '../utils';
import {
  MonetiseSettingsFormValues,
  MonetiseSettingsSchema,
  NewAssetsFormValues,
} from '../validation';
import Checkbox from './ui/Checkbox';
import { getCurrentUser } from '../api/user';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createMediaAssets } from '../api/media-assets';
import { TabPathes } from '../types';

type Props = {
  values: NewAssetsFormValues;
};

const MonetiseSettingsForm: React.FC<Props> = ({ values }) => {
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
    staleTime: 1000 * 30,
  });

  const form = useForm({
    resolver: zodResolver(MonetiseSettingsSchema),
    defaultValues: {
      price: '',
      percentage: 0,
      terms: false,
    },
  });

  const handleCopy = async () => {
    data?.walletAddress && copyToClipboard(data?.walletAddress);
  };

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (formData: FormData) => createMediaAssets(formData),
    onError: (error) => alert(`Error: ${error.message}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assets'] });
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      navigate('/' + TabPathes.ASSETS);
    },
  });

  const onSubmit = async (
    monetiseValues: MonetiseSettingsFormValues,
  ) => {
    const formData = new FormData();
    formData.append('name', values.name);

    const tags = values.metadata.map((tag) => tag.value);

    formData.append('tag_uuids', JSON.stringify(tags));
    formData.append('description', values.description);
    formData.append('content_url', values.content);
    formData.append('price', monetiseValues.price);
    formData.append('percentage', monetiseValues.percentage.toString());

    const isFile = values.document instanceof File;

    formData.append(
      isFile ? 'policy_source_file' : 'policy_source_url',
      values.document!,
    );
    values.thumbnail && formData.append('thumbnail', values.thumbnail);

    mutate(formData);
  };

  return (
    <div className='mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-row gap-2'>
          <BackButton />

          <div className='text-sm font-semibold'>
            <h2 className='text-green-blue-0'>New Asset</h2>

            <div className='text-green-blue-2'>Asset input details</div>
          </div>
        </div>

        <Input
          value={values.name}
          label='Asset Name'
          placeholder='Asset Name'
          id='Asset Name'
        />

        <div className='flex flex-col font-medium'>
          <h2 className='text-sm text-coral'>Asset info:</h2>

          <div className='scrollbar-hide mb-3 mt-2 flex w-full flex-row gap-2 overflow-x-auto p-1'>
            {values.metadata.map((tag, index) => (
              <Tag key={index} text={tag.label} active />
            ))}
          </div>

          <h3 className='text-sm font-semibold text-green-blue-0'>
            {values.content}
          </h3>
        </div>

        <Form {...form}>
          <form className='flex flex-1 flex-col gap-6 overflow-y-auto'>
            <FormField
              control={form.control}
              name='percentage'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <Range
                      {...field}
                      value={field.value}
                      setValue={(val) => field.onChange(val)}
                      label='Royalty %:'
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            {data?.walletAddress && (
              <div className='flex flex-col gap-1 font-medium'>
                <div className='text-sm text-coral'>Wallet:</div>

                <div className='flex flex-row items-center gap-1 text-sm text-green-blue-3'>
                  <Wallet />

                  {`${data.walletAddress.slice(
                    0,
                    5,
                  )} ... ${data.walletAddress.slice(-5)}`}
                </div>

                <div
                  className={`
                    flex cursor-pointer flex-row items-center gap-1 text-sm text-green-blue-3
                  `}
                  onClick={handleCopy}
                >
                  <Copy />
                  Copy address
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name='price'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <Input
                      {...field}
                      label='Set Up Your Price:'
                      placeholder='0.0'
                      currency
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='terms'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormControl>
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(val) => field.onChange(val)}
                      label={
                        <div
                          className={`
                            w-full text-left text-sm font-medium text-green-blue-2 transition-colors
                          `}
                        >
                          l acknowledge that I agree to the
                          <Link
                            to={'https://robocorp.co/terms-conditions'}
                            className={`
                              mx-1 bg-green-gradient bg-clip-text text-transparent underline
                              decoration-neon-green
                            `}
                          >
                            Terms of Use
                          </Link>
                          and have read the

                          <Link
                            to={'https://robocorp.co/privacy-policy'}
                            className={`
                              ml-1 bg-green-gradient bg-clip-text text-transparent underline
                              decoration-neon-green
                            `}
                          >
                            Privacy Policy
                          </Link>
                          .
                        </div>
                      }
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>

      <div className='mt-auto'>
        <Button
          type='submit'
          onClick={form.handleSubmit(onSubmit)}
          variant='solid'
          loading={isPending}
          iconBack={<ArrowsLine color='currentColor' />}
        >
          Create asset
        </Button>
      </div>
    </div>
  );
};

export default MonetiseSettingsForm;
